<template>
  <SectionShell id="proportion" num="07" kicker="The Face Map">
    <template #title>Six measurements that<br />catch <span class="hl">nine</span> out of ten<br />beginner mistakes.</template>
    <template #lede>
      <p>
        A grid gets the shapes in the right box. These rules tell you when the box was
        wrong. They are averages, not laws — real faces deviate, and the deviation is
        the likeness — but if your drawing breaks one of them badly, something is off
        and you now know where to look.
      </p>
    </template>

    <div class="map">
      <figure class="map__fig card">
        <svg viewBox="0 0 420 560" class="map__svg" role="img"
             aria-label="A front-view head with proportion guides for the eye line, facial thirds, eye spacing, nose width, mouth width and ear placement">
          <rect width="420" height="560" fill="#080b10" />

          <!-- faint head so the guides have something to sit on -->
          <g opacity="0.9">
            <ellipse cx="210" cy="250" rx="122" ry="160" fill="#121820" stroke="#39414b" stroke-width="1.2" />
            <path d="M168 400 L168 432 Q120 452 96 500 L96 560 M252 400 L252 432 Q300 452 324 500 L324 560"
                  fill="none" stroke="#39414b" stroke-width="1.2" />
            <!-- features, dimmed -->
            <g stroke="#5a636e" stroke-width="1.2" fill="none">
              <path d="M137 250 Q161 236 186 250 Q161 264 137 250Z" />
              <path d="M234 250 Q258 236 283 250 Q258 264 234 250Z" />
              <circle cx="161" cy="250" r="9" />
              <circle cx="258" cy="250" r="9" />
              <path d="M198 262 Q192 300 186 318 Q198 332 210 332 Q222 332 234 318 Q228 300 222 262" />
              <path d="M161 353 Q186 344 210 348 Q234 344 258 353 Q234 362 210 358 Q186 362 161 353Z" />
              <path d="M90 240 Q76 250 80 283 Q84 318 106 325" />
              <path d="M330 240 Q344 250 340 283 Q336 318 314 325" />
              <path d="M132 232 Q161 218 190 228 M230 228 Q259 218 288 232" />
            </g>
          </g>

          <!-- ---- guide: eye line ---- -->
          <g v-if="on.eyeline" class="g g--sig">
            <line x1="20" y1="250" x2="400" y2="250" />
            <line x1="210" y1="90" x2="210" y2="250" stroke-dasharray="4 5" />
            <line x1="210" y1="250" x2="210" y2="410" stroke-dasharray="4 5" />
            <g class="tick">
              <line x1="200" y1="90" x2="220" y2="90" />
              <line x1="200" y1="410" x2="220" y2="410" />
            </g>
            <text class="t" x="24" y="242">EYE LINE — HALFWAY DOWN THE HEAD</text>
            <text class="t" x="216" y="170">A</text>
            <text class="t" x="216" y="336">A</text>
          </g>

          <!-- ---- guide: thirds ---- -->
          <g v-if="on.thirds" class="g g--light">
            <line x1="40" y1="155" x2="380" y2="155" />
            <line x1="40" y1="240" x2="380" y2="240" />
            <line x1="40" y1="325" x2="380" y2="325" />
            <line x1="40" y1="410" x2="380" y2="410" />
            <text class="t" x="46" y="149">HAIRLINE</text>
            <text class="t" x="46" y="234">BROW</text>
            <text class="t" x="46" y="319">BASE OF NOSE</text>
            <text class="t" x="46" y="404">CHIN</text>
            <g class="brace">
              <line x1="368" y1="155" x2="368" y2="240" />
              <line x1="368" y1="240" x2="368" y2="325" />
              <line x1="368" y1="325" x2="368" y2="410" />
            </g>
            <text class="t" x="374" y="200">1/3</text>
            <text class="t" x="374" y="286">1/3</text>
            <text class="t" x="374" y="371">1/3</text>
          </g>

          <!-- ---- guide: five eye widths ---- -->
          <g v-if="on.fifths" class="g g--sig">
            <g class="tick">
              <line v-for="(x, i) in fifths" :key="i" :x1="x" y1="272" :x2="x" y2="288" />
            </g>
            <line x1="88" y1="280" x2="332" y2="280" />
            <text class="t" x="88" y="304">FIVE EYE-WIDTHS ACROSS, EYES ARE #2 AND #4</text>
            <g fill="rgba(86,225,240,0.16)" stroke="none">
              <rect x="137" y="236" width="49" height="28" />
              <rect x="234" y="236" width="49" height="28" />
            </g>
          </g>

          <!-- ---- guide: eye gap ---- -->
          <g v-if="on.gap" class="g g--light">
            <rect x="186" y="238" width="48" height="24" fill="rgba(255,180,92,0.18)" stroke="none" />
            <line x1="186" y1="250" x2="234" y2="250" />
            <text class="t" x="150" y="222">GAP BETWEEN THE EYES = ONE EYE</text>
          </g>

          <!-- ---- guide: nose width ---- -->
          <g v-if="on.nose" class="g g--sig">
            <line x1="186" y1="250" x2="186" y2="336" stroke-dasharray="4 5" />
            <line x1="234" y1="250" x2="234" y2="336" stroke-dasharray="4 5" />
            <line x1="186" y1="336" x2="234" y2="336" />
            <text class="t" x="242" y="332">NOSE WIDTH ≈ INNER EYE CORNERS</text>
          </g>

          <!-- ---- guide: mouth width ---- -->
          <g v-if="on.mouth" class="g g--light">
            <line x1="161" y1="250" x2="161" y2="366" stroke-dasharray="4 5" />
            <line x1="258" y1="250" x2="258" y2="366" stroke-dasharray="4 5" />
            <line x1="161" y1="366" x2="258" y2="366" />
            <text class="t" x="100" y="384">MOUTH CORNERS ≈ CENTRES OF THE EYES</text>
          </g>

          <!-- ---- guide: ears ---- -->
          <g v-if="on.ears" class="g g--violet">
            <line x1="60" y1="240" x2="360" y2="240" stroke-dasharray="4 5" />
            <line x1="60" y1="325" x2="360" y2="325" stroke-dasharray="4 5" />
            <rect x="74" y="240" width="36" height="85" fill="rgba(155,140,255,0.14)" stroke="none" />
            <rect x="310" y="240" width="36" height="85" fill="rgba(155,140,255,0.14)" stroke="none" />
            <text class="t" x="60" y="345">EAR SPANS BROW → NOSE BASE</text>
          </g>
        </svg>

        <figcaption class="map__cap mono">front view · adult · averages only</figcaption>
      </figure>

      <div class="map__side">
        <ul class="toggles">
          <li v-for="g in guides" :key="g.k">
            <button :class="{ 'is-on': on[g.k] }" :aria-pressed="String(on[g.k])" @click="on[g.k] = !on[g.k]">
              <span class="sw" :style="{ background: on[g.k] ? g.c : 'transparent', borderColor: g.c }"></span>
              {{ g.name }}
            </button>
            <p>{{ g.note }}</p>
          </li>
        </ul>

        <div class="map__btns">
          <button @click="all(true)">Show all</button>
          <button @click="all(false)">Clear</button>
        </div>

        <div class="warn card">
          <h4 class="mono">Where these rules bend</h4>
          <p>
            Children have proportionally larger craniums and the eye line sits lower on the
            face. Nose width and lip fullness vary widely between individuals and between
            populations — treat the "inner corners" rule as a starting hypothesis you then
            correct against the actual person in front of you.
          </p>
          <p>
            And the big one: <b>almost everyone draws the eyes too high.</b> If a portrait
            feels subtly wrong and you can't say why, measure from chin to eye line and
            from eye line to the top of the skull. They should match.
          </p>
        </div>
      </div>
    </div>
  </SectionShell>
</template>

<script setup>
import { reactive } from 'vue'
import SectionShell from '../ui/SectionShell.vue'

const fifths = [88, 137, 186, 234, 283, 332]

const on = reactive({
  eyeline: true,
  thirds: false,
  fifths: false,
  gap: false,
  nose: false,
  mouth: false,
  ears: false
})

const guides = [
  { k: 'eyeline', name: 'The eye line', c: '#56e1f0', note: 'The eyes sit halfway down the head — not a third of the way, which is where memory puts them. Measure chin-to-eyes against eyes-to-crown.' },
  { k: 'thirds', name: 'Facial thirds', c: '#ffb45c', note: 'Hairline to brow, brow to base of nose, base of nose to chin: three roughly equal bands.' },
  { k: 'fifths', name: 'Five eye-widths', c: '#56e1f0', note: 'The head at eye level is about five eye-widths wide. The eyes are the second and fourth.' },
  { k: 'gap', name: 'The gap', c: '#ffb45c', note: 'One whole eye fits between the two eyes. Beginners crowd them together, which ages and narrows the face.' },
  { k: 'nose', name: 'Nose width', c: '#56e1f0', note: 'Drop verticals from the inner corners of the eyes: that is roughly where the nostrils end.' },
  { k: 'mouth', name: 'Mouth width', c: '#ffb45c', note: 'Drop verticals from the centres of the eyes and you land near the corners of a relaxed mouth. A smile widens past it.' },
  { k: 'ears', name: 'Ear placement', c: '#9b8cff', note: 'Top of the ear at the brow, bottom at the base of the nose. In profile the ear sits about halfway between the back of the skull and the front of the eye.' }
]

function all(v) {
  Object.keys(on).forEach((k) => (on[k] = v))
}
</script>

<style scoped>
.map {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: clamp(24px, 3.5vw, 56px);
  align-items: start;
}
.map__fig { margin: 0; overflow: hidden; position: sticky; top: 92px; background: #080b10; }
.map__svg { display: block; width: 100%; height: auto; }
.map__cap {
  border-top: 1px solid var(--edge-soft);
  padding: 11px 14px;
  font-size: 9px;
  color: var(--text-faint);
}

.map__svg :deep(.g) { fill: none; stroke-width: 1.1; }
.g--sig { stroke: #56e1f0; }
.g--light { stroke: #ffb45c; }
.g--violet { stroke: #9b8cff; }
.g .tick { stroke-width: 1.4; }
.g .brace { stroke-dasharray: 2 4; }
.g .t {
  font-family: 'JetBrains Mono', monospace;
  font-size: 8.5px;
  letter-spacing: 1.2px;
  stroke: none;
}
.g--sig .t { fill: #7fe9f5; }
.g--light .t { fill: #ffc98a; }
.g--violet .t { fill: #b6acff; }

.toggles { list-style: none; margin: 0 0 20px; padding: 0; }
.toggles li { padding: 14px 0; border-bottom: 1px solid var(--edge-soft); }
.toggles button { display: inline-flex; align-items: center; gap: 10px; }
.toggles button.is-on { color: var(--ink-900); }
.sw { width: 9px; height: 9px; border: 1px solid; border-radius: 50%; display: inline-block; }
.toggles p { margin: 10px 0 0; color: var(--text-faint); font-size: 13px; line-height: 1.6; max-width: 50ch; }

.map__btns { display: flex; gap: 6px; margin-bottom: 26px; }

.warn { padding: 24px 26px; }
.warn h4 { color: var(--light); font-size: 10px; font-weight: 400; margin-bottom: 14px; }
.warn p { color: var(--text-dim); font-size: 14px; }
.warn b { color: var(--v5); font-weight: 400; }

@media (max-width: 980px) {
  .map { grid-template-columns: 1fr; }
  .map__fig { position: static; }
}
</style>
